
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type MapVisibilityContextType = {
    initializeMapVisibility: (scope: string, defaultValue: boolean) => void;
    clearMapVisibility: (scope: string) => void;
    getMapVisibility: (scope: string, defaultValue?: boolean) => boolean;
    setMapVisibility: (scope: string, value: boolean) => void;
    toggleMapVisibility: (scope: string, defaultValue?: boolean) => void;
};

const MapVisibilityContext = createContext<MapVisibilityContextType | undefined>(undefined);

export const MapVisibilityProvider = ({ children }: { children: React.ReactNode }) => {
    const [mapVisibility, setMapVisibilityState] = useState<Record<string, boolean>>({});

    const initializeMapVisibility = useCallback((scope: string, defaultValue: boolean) => {
        setMapVisibilityState((prev) => {
            if (scope in prev) {
                return prev;
            }
            return { ...prev, [scope]: defaultValue };
        });
    }, []);

    const getMapVisibility = useCallback(
        (scope: string, defaultValue: boolean = true) => {
            if (scope in mapVisibility) {
                return mapVisibility[scope];
            }
            return defaultValue;
        },
        [mapVisibility]
    );

    const setMapVisibility = useCallback((scope: string, value: boolean) => {
        setMapVisibilityState((prev) => {
            if (prev[scope] === value) {
                return prev;
            }
            return { ...prev, [scope]: value };
        });
    }, []);

    const toggleMapVisibility = useCallback((scope: string, defaultValue: boolean = true) => {
        setMapVisibilityState((prev) => {
            const current = scope in prev ? prev[scope] : defaultValue;
            return { ...prev, [scope]: !current };
        });
    }, []);

    const clearMapVisibility = useCallback((scope: string) => {
        setMapVisibilityState((prev) => {
            if (!(scope in prev)) return prev;
            const next = { ...prev };
            delete next[scope];
            return next;
        });
    }, []);

    return (
        <MapVisibilityContext.Provider value={{ 
            initializeMapVisibility,
            clearMapVisibility,
            getMapVisibility,
            setMapVisibility,
            toggleMapVisibility,
        }}>
            {children}
        </MapVisibilityContext.Provider>
    );
};

export const useMapVisibilityContext = () => {
    const context = useContext(MapVisibilityContext);
    if (!context) throw new Error('useMapVisibilityContext must be used within a MapVisibilityProvider');
    return context;
};

type UseMapVisibilityOptions = {
    scope?: string;
    defaultVisible?: boolean;
};

/**
 * Hook to manage map visibility for a specific scope.
 * This is the primary way to interact with map visibility in components.
 */
export const useMapVisibility = (options?: UseMapVisibilityOptions) => {
    const {
        initializeMapVisibility,
        clearMapVisibility,
        getMapVisibility,
        setMapVisibility,
        toggleMapVisibility,
    } = useMapVisibilityContext();
    const pathname = useLocation().pathname;
    const scope = options?.scope ?? (pathname || 'default');
    const defaultVisible = options?.defaultVisible ?? true;

    useEffect(() => {
        initializeMapVisibility(scope, defaultVisible);
        // Clear on unmount so the next mount re-initializes with the correct defaultVisible
        return () => {
            clearMapVisibility(scope);
        };
    }, [scope, defaultVisible, initializeMapVisibility, clearMapVisibility]);

    const isMapVisible = getMapVisibility(scope, defaultVisible);

    const showMap = useCallback(() => {
        setMapVisibility(scope, true);
    }, [scope, setMapVisibility]);

    const hideMap = useCallback(() => {
        setMapVisibility(scope, false);
    }, [scope, setMapVisibility]);

    const toggleMap = useCallback(() => {
        toggleMapVisibility(scope, defaultVisible);
    }, [scope, toggleMapVisibility, defaultVisible]);

    const setMapVisible = useCallback(
        (value: boolean) => {
            setMapVisibility(scope, value);
        },
        [scope, setMapVisibility]
    );

    return {
        isMapVisible,
        showMap,
        hideMap,
        toggleMapVisibility: toggleMap,
        setMapVisible,
    };
};

export { MapVisibilityContext };

